import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { SupportTicketsService } from '../../shared/services/admin/support-tickets.service';
import { ApiService } from '../../shared/services/api/api.service';
import * as TicketsActions from './tickets.actions';
import { selectTicketsLastFilter } from './tickets.selectors';

@Injectable()
export class TicketsEffects {
  private actions$ = inject(Actions);
  private ticketsService = inject(SupportTicketsService);
  private apiService = inject(ApiService);
  private store = inject(Store);

  loadTickets$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TicketsActions.loadTickets),
        withLatestFrom(this.store.select(selectTicketsLastFilter)),
        switchMap(([action, lastFilter]) => {
          const params: any = {
            page: action.page || lastFilter?.page || 1,
            limit: action.pageSize || lastFilter?.pageSize || 20,
          };
          if (action.status && action.status !== 'all') {
            params.status = action.status;
          } else if (lastFilter?.status && lastFilter.status !== 'all') {
            params.status = lastFilter.status;
          }

          return this.ticketsService.getTickets(params, false).pipe(
            map((response) => TicketsActions.loadTicketsSuccess({ response })),
            catchError((error) => {
              this.apiService.showError(error.message || 'Failed to load tickets', 'Error');
              return of(TicketsActions.loadTicketsFailure({ error }));
            }),
          );
        }),
      );
    },
    { functional: false },
  );

  loadTicketById$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TicketsActions.loadTicketById),
        switchMap(({ ticketId }) =>
          this.ticketsService.getTicketById(ticketId, false).pipe(
            map((ticket) => TicketsActions.loadTicketByIdSuccess({ ticket })),
            catchError((error) => {
              this.apiService.showError(error.message || 'Failed to load ticket', 'Error');
              return of(TicketsActions.loadTicketByIdFailure({ error }));
            }),
          ),
        ),
      );
    },
    { functional: false },
  );

  updateTicketStatus$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TicketsActions.updateTicketStatus),
        switchMap(({ ticketId, update }) =>
          this.ticketsService.updateTicketStatus(ticketId, update).pipe(
            map((ticket) => {
              this.apiService.showSuccess('Ticket status updated successfully', 'Success');
              return TicketsActions.updateTicketStatusSuccess({ ticket });
            }),
            catchError((error) => {
              this.apiService.showError(error.message || 'Failed to update ticket', 'Error');
              return of(TicketsActions.updateTicketStatusFailure({ error }));
            }),
          ),
        ),
      );
    },
    { functional: false },
  );
}
